import { EnumAiAutomationLevel, EnumAlertStatus } from '@/lib/api/types.gen';
import { DispositionWorkflowConfig } from '../Sites/siteApi';

export interface AlertFetchedForMonitor {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  alert?: MonitorAlertFullDto;
}

export interface DispositionWorkflowHistory {
  statusHistories?: {
    [key in EnumAlertStatus]?: {
      stepIndex: number;
      completedAt: string;
      note: string | null;
      actioned: boolean;
    }[];
  };
}

export interface MonitorAlertFullDto {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  note: any;
  id?: string;
  projectId?: string;
  projectName?: string;
  notified?: boolean;
  notifiedAt?: Date;
  lastEvent?: Date;
  actionedBy?: string;
  disarmedAt?: Date;
  disarmedBy?: string;
  actionedAt?: Date;
  closedAt?: Date;
  recentNotes?: AlertNoteDto[];
  occurred?: Date;
  companyId?: string;
  companyName?: string;
  status?: EnumAlertStatus;
  project?: MonitorAlertProjectDto;
  contactEvents?: ContactEventDto[];
  alertId?: string;
  events?: MonitorAlertEventDto[];
  recentAlerts?: AssociatedMonitoringAlert[];
  intruderEventData?: IntruderEventData;
  nextActionSlaDue?: Date;
  noThreatDisarmMinutes?: number;
  snapshot?: string;
  dispositionWorkflowConfig?: DispositionWorkflowConfig;
  disabledDispositions?: string[];
  dispositionWorkflowHistory?: DispositionWorkflowHistory;
}

export interface AdminUserDto {
  tokenExpiry?: Date;
  token?: string;
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  number?: string;
  smsAuthorised?: boolean;
  roles?: string[];
  lockedOut?: boolean;
  userName?: string;
  tenantId?: string;
}

export interface ContactEventDto {
  /**  */
  occurred?: Date;

  /**  */
  to?: string;

  /**  */
  toId?: string;

  /**  */
  sentBy?: string;

  /**  */
  type?: EnumContactEventType;

  /**  */
  body?: string;

  /**  */
  successful?: boolean;

  /**  */
  parentEventId?: string;

  /**  */
  pending?: boolean;

  /**  */
  id?: string;
}

export enum EnumContactEventType {
  'Police' = 'Police',
  'ContactEmail' = 'ContactEmail',
  'ContactSms' = 'ContactSms',
  'ContactSmsReply' = 'ContactSmsReply',
  'ContactCall' = 'ContactCall',
  'MonitoringNotified' = 'MonitoringNotified',
}

export interface AssociatedMonitoringAlert {
  /**  */
  id?: string;

  /**  */
  alertId?: string;

  /**  */
  status?: EnumAlertStatus;

  /**  */
  notifiedAt?: Date;

  /**  */
  event?: MonitorAlertEventDto;

  /**  */
  companyId?: string;

  /**  */
  projectId?: string;

  /**  */
  closed?: boolean;
}

export enum EnumMonitoringEventType {
  'Unknown' = 'Unknown',
  'CameraOnline' = 'CameraOnline',
  'CameraOffline' = 'CameraOffline',
  'CameraTampered' = 'CameraTampered',
  'CameraFailRecord' = 'CameraFailRecord',
  'CameraOverheated' = 'CameraOverheated',
  'CameraMotion' = 'CameraMotion',
  'PersonSeen' = 'PersonSeen',
  'PersonLoitering' = 'PersonLoitering',
  'PersonCount' = 'PersonCount',
  'PersonWallScale' = 'PersonWallScale',
  'VehicleSeen' = 'VehicleSeen',
  'Analytic' = 'Analytic',
  'SoundDetected' = 'SoundDetected',
}

export interface MonitorAlertEventDto {
  /**  */
  id: string;

  /**  */
  occurred?: string;

  /**  */
  type?: EnumMonitoringEventType;

  /**  */
  videoProcessorId?: string;

  /**  */
  videoProcessorIdentifier?: string;

  videoProcessorDescription?: string;

  /**  */
  videoProcessor?: EnumVideoProcessor;

  /**  */
  activationZone?: string;

  /**  */
  values?: MonitoringEventValueSearchDto[];

  /**  */
  nvrId?: string;

  /**  */
  nvrDeviceName?: string;

  /**  */
  firstViewedAt?: Date;
}

export interface MonitoringEventValueSearchDto {
  /**  */
  id?: string;

  /**  */
  type?: MonitoringEventValueType;

  /**  */
  fetched?: boolean;

  /**  */
  value?: number;

  /**  */
  txtValue?: string;

  /**  */
  sourceUrl?: string;

  /**  */
  probability?: number;
}

export enum MonitoringEventValueType {
  'SnapshotImage' = 'SnapshotImage',
  'PreviewVideo' = 'PreviewVideo',
  'PersonCount' = 'PersonCount',
  'PersonSeen' = 'PersonSeen',
  'VehicleSeen' = 'VehicleSeen',
  'Unknown' = 'Unknown',
  'AnimalSeen' = 'AnimalSeen',
  'PreviewVideoLink' = 'PreviewVideoLink',
  'PreviewVideoMp4' = 'PreviewVideoMp4',
  'PreviewVideoHls' = 'PreviewVideoHls',
}

export interface IntruderEventData {
  /**  */
  policeId?: string;

  /**  */
  weapons?: string;

  /**  */
  notes?: string;

  /**  */
  intruders?: IntruderProfile[];

  /**  */
  eventOverview?: string;

  /**  */
  closureResult?: string;

  /**  */
  offenceType?: string;

  /**  */
  policeResponded?: boolean;

  /**  */
  videoClip?: IntruderVideoClip;

  /** Selected event ID for report video clip */
  reportEventId?: string;
}

export type IntruderVideoClipStatus = 'Pending' | 'Processing' | 'Ready' | 'Failed';

export type IntruderVideoClip = {
  cloudflareStreamId?: string;
  cloudflarePlaybackUrl?: string;
  cloudflareEmbedUrl?: string;
  cloudflareDownloadUrl?: string;
  thumbnailUrl?: string;
  eventId?: string;
  startTime?: string;
  endTime?: string;
  capturedAt?: string;
  expiresAt?: string;
  status?: IntruderVideoClipStatus;
  errorMessage?: string;
}

export interface IntruderProfile {
  id: string;

  /**  */
  images?: IntruderSnapshot[] | null;

  /**  */
  number?: number;

  /**  */
  sex?: string;

  /**  */
  age?: string;

  /**  */
  build?: string;

  /**  */
  height?: string;

  /**  */
  ethnicity?: string;

  /**  */
  hair?: string;

  /**  */
  description?: string;

  /**  */
  clothing?: string;

  /**  */
  clothingShoes?: string;

  /**  */
  clothingPants?: string;

  /**  */
  clothingTop?: string;

  /**  */
  otherInfo?: string;

  vehicleType?: string;
  vehicleColor?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  licensePlate?: string;
  occupants?: string;
  vehicleDistinguishingFeatures?: string;
}

export interface IntruderSnapshot {
  /**  */
  eventValueId?: string;

  /**  */
  topPct?: number;

  /**  */
  leftPct?: number;

  /**  */
  widthPct?: number;

  /**  */
  heightPct?: number;
}

export interface MonitorAlertProjectDto {
  /**  */
  name?: string;

  /**  */
  siteDisarmedUntil?: Date;

  /**  */
  newMonitoringAlertsAfterSeconds?: number;

  /**  */
  siteDisarmedBy?: string;

  /**  */
  siteNotes?: string;

  /**  */
  assets?: MonitorAssetDto[];

  /**  */
  contacts?: ProjectContactDto[];

  /**  */
  guardResponseRequired?: boolean;

  /**  */
  monitoredBy?: ProjectMonitoringDto[];

  /**  */
  guardResponseDispatchCode?: string;

  /**  */
  address?: AddressDto;

  /**  */
  aiPrimed?: boolean;

  timeZone: string;
  aiAutomationLevel: EnumAiAutomationLevel;
}

export interface ProjectMonitoringDto {
  /**  */
  monitoringCompanyName?: string;

  /**  */
  monitoringCompanyId?: string;

  /**  */
  externalId?: string;

  /**  */
  order?: number;
}

export interface ProjectContactDto {
  /**  */
  id?: string;

  /**  */
  projectId?: string;

  /**  */
  name?: string;

  /**  */
  email?: string;

  /**  */
  phone?: string;

  /**  */
  order?: number;

  /**  */
  role?: string;

  /**  */
  contactPreference?: EnumContactPreference;

  /**  */
  companyId?: string;

  /**  */
  companyName?: string;

  /**  */
  lastName?: string;

  /**  */
  contactId?: string;

  /**  */
  contactCompany?: string;
}

export enum EnumContactPreference {
  'SmsThenCall' = 'SmsThenCall',
  'SmsOnly' = 'SmsOnly',
  'CallOnly' = 'CallOnly',
  'None' = 'None',
}

export interface MonitorAssetDto {
  /**  */
  id?: string;

  /**  */
  name?: string;

  /**  */
  serialNumber?: string;

  /**  */
  address?: AddressDto;

  /**  */
  cameras?: MonitorAssetCamera[];

  /**  */
  deletedAt?: Date;
}

export enum EnumVideoProcessor {
  'UmboCv' = 'UmboCv',
  'ThreeDEye' = 'ThreeDEye',
  'Smtp' = 'Smtp',
  'Milestone' = 'Milestone',
  'Camect' = 'Camect',
  'NxWitness' = 'NxWitness',
  'HikVisionNvr' = 'HikVisionNvr',
  'DahuaCamera' = 'DahuaCamera',
  'OpenEye' = 'OpenEye',
  'IntegrationHub' = 'IntegrationHub'
}

export interface MonitorAssetCamera {
  /**  */
  id?: string;

  /**  */
  videoProcessor?: EnumVideoProcessor;

  /**  */
  videoProcessorId?: string;

  /**  */
  notes?: string;
}

export interface AddressDto {
  /**  */
  addressLine1?: string;

  /**  */
  addressLine2?: string;

  /**  */
  city?: string;

  /**  */
  postCode?: string;

  /**  */
  region?: string;

  /**  */
  country?: string;

  /**  */
  lat?: number;

  /**  */
  lon?: number;
}

export interface AlertNoteDto {
  /**  */
  id?: string;

  /**  */
  note?: string;

  /**  */
  author?: string;

  /**  */
  date?: Date;

  metadata?: MonitoringAlertNoteMetadata | null;
}

export interface QuillAnalysisMetadata {
  type: 'QuillAnalysis';
  description: string;
  status: EnumAlertStatus;
  monitoringEventId: string;
}

export interface QuillEscalationMetadata {
  type: 'QuillEscalation';
  previousStatus: EnumAlertStatus;
  newStatus: EnumAlertStatus;
  monitoringEventId: string;
}

export interface QuillAutoCloseMetadata {
  type: 'QuillAutoClose';
  previousStatus: EnumAlertStatus;
  newStatus: EnumAlertStatus;
  monitoringEventId: string;
}

export type MonitoringAlertNoteMetadata =
  | QuillAnalysisMetadata
  | QuillEscalationMetadata
  | QuillAutoCloseMetadata;

export interface AlertTimelineNoteDto {
  /**  */
  id?: string;

  /**  */
  note?: string;

  /**  */
  author?: string;

  /**  */
  date?: Date;

  image?: IntruderSnapshot | null;

  metadata?: MonitoringAlertNoteMetadata | null;
}

export enum CallResult {
  ConfirmedStaff = 'ConfirmedStaff',
  ConfirmedIntruder = 'ConfirmedIntruder',
  CustomerInvestigating = 'CustomerInvestigating',
  LeftMessage = 'LeftMessage',
  NoVoicemail = 'NoVoicemail',
  WrongNumber = 'WrongNumber',
  CustomerBusy = 'CustomerBusy',
}

export type AlertAction =
  | 'initial'
  | 'nothreat'
  | 'suspicious'
  | 'vehicle'
  | 'intruder'
  | 'irrelevant'
  | 'person-or-vehicle'
  | 'staff'
  | 'confirmed-staff'
  | 'no-threat'
  | 'weather'
  | 'police'
  | 'guard'
  | 'false-alarm'
  | 'request-service'
  | 'weather'
  | 'animal-insect'
  | 'environment'
  | 'light'
  | 'incorrect-grid'
  | 'no-further-action'
  | 'quill-staff'
  | 'other'
  | 'safety-event';

export type AlertStep =
  | 'initial'
  | 'nothreat'
  | 'suspicious'
  | 'vehicle'
  | 'intruder'
  | 'no-further-action'
  | 'person-detected'
  | 'incorrect-grid'
  | 'staff'
  | 'call-options'
  | 'false-alarm'
  | 'request-service'
  | 'weather'
  | 'animal-insect'
  | 'environment'
  | 'light'
  | 'confirmed-staff'
  | 'quill-staff'
  | 'other'
  | 'safety-event';

export const statusConfig = {
  New: { color: 'bg-blue-600', name: 'New' },
  Weather: { color: 'bg-cyan-700', name: 'Weather' },
  Other: { color: 'bg-gray-900', name: 'Other' },
  FalsePositive: { color: 'bg-gray-900', name: 'False Alarm' },
  PossiblyStaff: { color: 'bg-gray-900', name: 'Possibly Staff' },
  ConfirmedStaff: { color: 'bg-gray-900', name: 'Confirmed Staff' },
  Intruder: { color: 'bg-red-800', name: 'Possibly Intruder' },
  Unknown: { color: 'bg-gray-900', name: 'Unknown' },
  NoThreat: { color: 'bg-gray-900', name: 'No Threat' },
  SuspiciousPerson: { color: 'bg-red-800', name: 'Suspicious Person' },
  AnimalOrInsect: { color: 'bg-gray-900', name: 'Animal or Insect' },
  Environment: { color: 'bg-gray-900', name: 'Environment' },
  LightReflection: { color: 'bg-gray-900', name: 'Light Reflection' },
  NeedsService: { color: 'bg-gray-900', name: 'Needs Service' },
  IncorrectMotionGrids: { color: 'bg-gray-900', name: 'Incorrect Motion Grids' },
  SafetyEvent: { color: 'bg-orange-600', name: 'Fall' },
} satisfies Record<EnumAlertStatus, { color: string; name: string }>;

export interface ActionOption {
  label: string;
  action: string;
  angle: number;
  color: string;
}

export interface InteractionTabProps {
  projectId: string;
  companyId: string;
  useAlertEvents: boolean;
  alertId?: string;
  videoProcessorId?: string;
}

export const CloseAlertMutationKey = 'closeAlert';
export const UpdateSuppressionMutationKey = 'updateSuppression';
